/* ==========================================================================
   H3O brand layer — shared behaviors.
   - Scroll reveals (.h3o-reveal / .h3o-stagger)
   - Count-up stats ([data-h3o-countup])
   - Marquee track duplication (.h3o-marquee)
   Loaded globally (deferred) from layout/theme.liquid.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Scroll reveals ---------------------------------------------------- */
  function initReveals(root) {
    var nodes = (root || document).querySelectorAll('.h3o-reveal:not(.is-revealed)');
    if (!nodes.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---- Count-up stats -----------------------------------------------------
     <span data-h3o-countup data-value="5" data-decimals="0" data-suffix="g">0</span>
  --------------------------------------------------------------------------- */
  function animateCount(el) {
    var target = parseFloat(el.dataset.value || el.textContent) || 0;
    var decimals = parseInt(el.dataset.decimals || '0', 10);
    var prefix = el.dataset.prefix || '';
    var suffix = el.dataset.suffix || '';
    var duration = parseInt(el.dataset.duration || '1200', 10);

    if (reduceMotion) {
      el.textContent = prefix + target.toFixed(decimals).replace(/\.0+$/, '') + suffix;
      return;
    }

    var start = null;
    function frame(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      var val = (target * eased).toFixed(decimals);
      if (decimals === 0) val = Math.round(target * eased).toString();
      el.textContent = prefix + val + suffix;
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function initCountups(root) {
    var nodes = (root || document).querySelectorAll('[data-h3o-countup]:not([data-counted])');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.setAttribute('data-counted', ''); animateCount(n); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-counted', '');
            animateCount(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---- Marquee ------------------------------------------------------------
     Markup: .h3o-marquee > .h3o-marquee__track (single copy of content).
     JS clones the track until it spans 2x the container for a seamless loop.
  --------------------------------------------------------------------------- */
  function initMarquees(root) {
    (root || document).querySelectorAll('.h3o-marquee:not([data-marquee-ready])').forEach(function (mq) {
      var track = mq.querySelector('.h3o-marquee__track');
      if (!track) return;
      mq.setAttribute('data-marquee-ready', '');
      var clones = 1;
      while (track.scrollWidth * (clones) < mq.clientWidth * 2 && clones < 6) {
        var clone = track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        mq.appendChild(clone);
        clones++;
      }
    });
  }

  /* ---- Carousel dots --------------------------------------------------------
     Mobile swipe containers (.h3o-hscroll, story rows) get a dot rail so the
     user can SEE there are more cards. Dots track scroll position and are
     tappable. Desktop hides the rail in CSS (containers are grids there).
  --------------------------------------------------------------------------- */
  function initCarouselDots(root) {
    var carousels = (root || document).querySelectorAll(
      '.h3o-hscroll:not([data-dots-ready]), .h3o-story__rows:not([data-dots-ready])'
    );

    carousels.forEach(function (car) {
      car.setAttribute('data-dots-ready', '');
      var items = Array.prototype.slice.call(car.children);
      if (items.length < 2) return;

      var rail = document.createElement('div');
      rail.className = 'h3o-dots';
      rail.setAttribute('aria-hidden', 'true');

      items.forEach(function (item, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'h3o-dots__dot' + (i === 0 ? ' is-active' : '');
        dot.tabIndex = -1;
        dot.addEventListener('click', function () {
          car.scrollTo({ left: item.offsetLeft - car.offsetLeft, behavior: 'smooth' });
        });
        rail.appendChild(dot);
      });

      car.insertAdjacentElement('afterend', rail);

      var ticking = false;
      function update() {
        ticking = false;
        var mid = car.scrollLeft + car.clientWidth / 2;
        var best = 0;
        var bestD = Infinity;
        items.forEach(function (item, i) {
          var center = item.offsetLeft - car.offsetLeft + item.offsetWidth / 2;
          var d = Math.abs(center - mid);
          if (d < bestD) { bestD = d; best = i; }
        });
        Array.prototype.forEach.call(rail.children, function (dot, i) {
          dot.classList.toggle('is-active', i === best);
        });
      }

      car.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
    });
  }

  function initAll(root) {
    initReveals(root);
    initCountups(root);
    initMarquees(root);
    initCarouselDots(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Re-init inside theme editor when sections re-render */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.H3O = window.H3O || {};
  window.H3O.initAll = initAll;
})();
